/**
 * Mobile/tablet: sync dots + section backdrop to the active slide.
 * One-shot micro-nudge teaches horizontal swipe without a peek layout.
 */
(function () {
  var MQ = window.matchMedia('(max-width: 899px)');
  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');

  function parseRgb(color) {
    if (!color) return null;
    var hex = color.trim();
    if (hex.charAt(0) === '#') {
      hex = hex.slice(1);
      if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
      }
      if (hex.length !== 6) return null;
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
      };
    }
    var m = hex.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
    if (!m) return null;
    return { r: +m[1], g: +m[2], b: +m[3] };
  }

  function isLightColor(color) {
    var rgb = parseRgb(color);
    if (!rgb) return false;
    var y = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
    return y > 0.55;
  }

  function init(root) {
    if (!root || root.dataset.wpmStoriesReady === 'true') return;
    root.dataset.wpmStoriesReady = 'true';

    var track = root.querySelector('[data-wpm-stories-track]');
    var slides = track ? track.querySelectorAll('[data-wpm-stories-slide]') : [];
    var dots = root.querySelectorAll('[data-wpm-stories-dot]');

    if (!track || slides.length < 2) return;

    function indexFromScroll() {
      var best = 0;
      var bestDist = Infinity;
      var mid = track.scrollLeft + track.clientWidth / 2;
      slides.forEach(function (slide, i) {
        var center = slide.offsetLeft + slide.offsetWidth / 2;
        var dist = Math.abs(center - mid);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    }

    function syncBackdrop(i) {
      var slide = slides[i];
      if (!slide) return;
      var bg = slide.getAttribute('data-panel-bg') || '#101716';
      root.style.setProperty('--wpm-service-stories-bg', bg);
      root.classList.toggle('is-on-light', isLightColor(bg));
    }

    function setActive(i) {
      dots.forEach(function (dot) {
        var di = Number(dot.getAttribute('data-wpm-stories-index'));
        var on = di === i;
        dot.classList.toggle('is-active', on);
        if (dot.getAttribute('tabindex') !== '-1') {
          dot.setAttribute('aria-current', on ? 'true' : 'false');
        }
      });
      syncBackdrop(i);
    }

    function onScroll() {
      if (!MQ.matches) return;
      setActive(indexFromScroll());
    }

    function goTo(i) {
      var slide = slides[i];
      if (!slide) return;
      track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      setActive(i);
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var i = Number(dot.getAttribute('data-wpm-stories-index'));
        if (!Number.isNaN(i)) goTo(i);
      });
    });

    track.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    function scrollHashIntoTrack() {
      if (!MQ.matches || !location.hash) return false;
      var id = location.hash.slice(1);
      if (!id) return false;
      var escape =
        window.CSS && CSS.escape
          ? CSS.escape
          : function (s) {
              return s.replace(/"/g, '\\"');
            };
      var target = track.querySelector('#' + escape(id));
      if (!target) return false;
      var slide = target.closest('[data-wpm-stories-slide]');
      var i = slide ? Array.prototype.indexOf.call(slides, slide) : -1;
      if (i < 0) return false;
      requestAnimationFrame(function () {
        goTo(i);
      });
      return true;
    }

    function runMicroNudge() {
      if (!MQ.matches || REDUCE.matches) return;
      if (root.dataset.wpmStoriesNudged === 'true') return;
      if (track.scrollLeft > 2) return;

      root.dataset.wpmStoriesNudged = 'true';

      function cleanup() {
        root.classList.remove('is-nudging');
        track.removeEventListener('pointerdown', cancel);
        track.removeEventListener('touchstart', cancel);
        track.removeEventListener('wheel', cancel);
        if (slides[0]) slides[0].removeEventListener('animationend', onEnd);
      }

      function cancel() {
        cleanup();
      }

      function onEnd(event) {
        if (event.target !== slides[0]) return;
        cleanup();
      }

      track.addEventListener('pointerdown', cancel, { once: true, passive: true });
      track.addEventListener('touchstart', cancel, { once: true, passive: true });
      track.addEventListener('wheel', cancel, { once: true, passive: true });
      if (slides[0]) slides[0].addEventListener('animationend', onEnd);

      root.classList.add('is-nudging');
    }

    function armMicroNudge() {
      if (!MQ.matches || REDUCE.matches) return;
      if (root.dataset.wpmStoriesNudged === 'true') return;

      if (!('IntersectionObserver' in window)) {
        window.setTimeout(runMicroNudge, 400);
        return;
      }

      var io = new IntersectionObserver(
        function (entries) {
          var entry = entries[0];
          if (!entry || !entry.isIntersecting) return;
          io.disconnect();
          window.setTimeout(runMicroNudge, 280);
        },
        { threshold: 0.45 }
      );
      io.observe(root);
    }

    setActive(0);
    var hashed = scrollHashIntoTrack();
    if (!hashed) armMicroNudge();
    window.addEventListener('hashchange', scrollHashIntoTrack);
  }

  function boot() {
    document.querySelectorAll('[data-wpm-service-stories]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-wpm-service-stories]');
    if (root) {
      delete root.dataset.wpmStoriesReady;
      delete root.dataset.wpmStoriesNudged;
      init(root);
    }
  });
})();
